import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Button, Stack, Typography } from '@mui/material'

import BulkVisibilityAction from './BulkVisibilityAction'
import useFilters, { toSitesUnscopeVariables, useSelection } from './store'
import { OPERATION_COUNT_SITES_UNSCOPED } from './operations-gql/queries'
import { SiteUnscope } from './types'

type CountSitesUnscopeData = {
  count: number
}

type SelectionBannerProps = {
  sites: SiteUnscope[]
}

const formatCount = (count: number) => new Intl.NumberFormat('fr-FR').format(count)

export const CounterBanner = () => {
  const [filters] = useFilters()
  const { t } = useTranslation()
  const { data, loading } = useQuery<CountSitesUnscopeData>(OPERATION_COUNT_SITES_UNSCOPED, {
    variables: toSitesUnscopeVariables(filters)
  })
  if (loading || !data) {
    return null
  }
  return (
    <Stack alignSelf={'flex-end'}>
      <Typography variant="caption" sx={{ color: 'primary.main' }}>{t('admin_sites.selection.counter_sites', { count: data.count, replace: { count: formatCount(data.count) } })}</Typography>
    </Stack>
  )
}

const SelectionBanner = ({ sites }: SelectionBannerProps) => {
  const { t } = useTranslation()
  const [filters] = useFilters()
  const { isAllSelected, selectedIds, hasSelection, areAllSelected, selectAll, clear } = useSelection()

  const areAllLoadedSelected = areAllSelected(sites.map(({ id }) => id))
  const shouldFetchTotal = isAllSelected || areAllLoadedSelected

  const { data } = useQuery<CountSitesUnscopeData>(OPERATION_COUNT_SITES_UNSCOPED, {
    variables: toSitesUnscopeVariables(filters),
    skip: !shouldFetchTotal
  })

  if (!hasSelection) return null

  const total = data?.count ?? null
  const selectedCount = isAllSelected ? total : selectedIds.length
  const canSelectAll = !isAllSelected && areAllLoadedSelected && total !== null && total > selectedIds.length

  const getMessage = () => {
    if (isAllSelected) return t('admin_sites.selection.all_selected', { count: total ?? 0 })
    return t('admin_sites.selection.loaded_selected', { count: selectedIds.length })
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ mb: 2, px: 2, py: 0.5, borderRadius: '8px', bgcolor: '#EBF2FE' }}
    >
      <Stack direction="row" alignItems="center" spacing={1} role="status">
        <Typography variant="caption" sx={{ color: 'content.secondary' }}>
          {getMessage()}
        </Typography>
        {canSelectAll && (
          <Button size="small" variant="text" onClick={selectAll} sx={{ fontSize: 12, textTransform: 'none' }}>
            {t('admin_sites.selection.select_all', { count: total })}
          </Button>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          onClick={clear}
          startIcon={<i className="fa-regular fa-xmark" aria-hidden="true" />}
          sx={{
            height: 28,
            px: 1.5,
            borderRadius: '8px',
            fontSize: 12,
            fontWeight: 400,
            textTransform: 'none',
            color: 'content.secondary',
            bgcolor: 'transparent',
            borderColor: '#C9DBFB',
            '& .MuiButton-startIcon > i': { fontSize: 12 },
            '&:hover': { bgcolor: 'rgba(37, 112, 235, 0.06)', borderColor: '#A9C6F9' }
          }}
        >
          {t('admin_sites.selection.clear')}
        </Button>
        <BulkVisibilityAction selectedCount={selectedCount} />
      </Stack>
    </Stack>
  )
}

export default SelectionBanner
